import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Heart } from "lucide-react";

const PRIMARY_LINKS = [
  { label: "Chi siamo", to: "/chi-siamo" },
  { label: "Progetti", to: "/progetti" },
  { label: "News", to: "/news" },
  { label: "Eventi", to: "/eventi" },
];

const PARTECIPA_LINKS = [
  { label: "Diventa volontario", to: "/partecipa" },
  { label: "Diventa socio", to: "/associarsi" },
  { label: "Dona", to: "/dona" },
  { label: "Raccolta olive", to: "/raccolta-olive" },
  { label: "Shop", to: "/shop" },
  { label: "Segnalazioni", to: "/segnalazioni" },
  { label: "Monte Faeta", to: "/monte-faeta" },
];

const SECONDARY_LINKS = [
  { label: "FAQ", to: "/faq" },
  { label: "Contatti", to: "/contatti" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [partDrop, setPartDrop] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setPartDrop(false);
  }, [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${
        scrolled ? "bg-white border-b border-[#E5E1D8] py-2 shadow-sm" : "bg-white/95 py-3"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between gap-4">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3">
          <img src="/logo-per-asciano-dark.png" alt="Per Asciano logo" className="h-10 sm:h-11 w-auto select-none" draggable="false" />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-base font-black tracking-[0.18em] text-[#1A1A1A] uppercase">Per Asciano</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5C5C] mt-0.5">Associazione territoriale</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-5 xl:gap-6">
          {PRIMARY_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[14px] font-semibold transition-colors ${
                  isActive ? "text-[#2E4F3B] underline underline-offset-[6px] decoration-2 decoration-[#C15C3D]" : "text-[#1A1A1A] hover:text-[#2E4F3B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          {/* Partecipa dropdown */}
          <div className="relative" onMouseEnter={() => setPartDrop(true)} onMouseLeave={() => setPartDrop(false)}>
            <button
              type="button"
              data-testid="nav-partecipa-trigger"
              className="text-[14px] font-semibold text-[#1A1A1A] hover:text-[#2E4F3B] inline-flex items-center gap-1"
            >
              Partecipa
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${partDrop ? "rotate-180" : ""}`} />
            </button>
            {partDrop && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 rounded-xl bg-white border-2 border-[#E5E1D8] shadow-xl py-2">
                {PARTECIPA_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-partecipa-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block px-4 py-2.5 text-sm font-semibold transition-colors ${
                        isActive ? "text-[#2E4F3B] bg-[#FBF9F6]" : "text-[#1A1A1A] hover:bg-[#FBF9F6] hover:text-[#2E4F3B]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {SECONDARY_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[14px] font-semibold transition-colors ${
                  isActive ? "text-[#2E4F3B] underline underline-offset-[6px] decoration-2 decoration-[#C15C3D]" : "text-[#1A1A1A] hover:text-[#2E4F3B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/dona" data-testid="navbar-dona-btn" className="hidden sm:inline-flex items-center gap-2 rounded-lg border-2 border-[#2E4F3B] text-[#2E4F3B] hover:bg-[#2E4F3B] hover:text-white font-semibold text-sm px-4 py-2 transition-colors">
            <Heart className="w-4 h-4" fill="currentColor" /> Dona
          </Link>
          <Link to="/partecipa" data-testid="navbar-cta-btn" className="hidden sm:inline-flex items-center gap-2 rounded-lg bg-[#C15C3D] hover:bg-[#a64830] text-white font-semibold text-sm px-4 py-2.5 transition-colors">
            Partecipa
          </Link>
          <button type="button" data-testid="navbar-menu-toggle" className="lg:hidden w-11 h-11 flex items-center justify-center rounded-lg border border-[#E5E1D8] bg-white" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="navbar-mobile-menu" className="lg:hidden absolute top-full inset-x-0 bg-white border-b border-[#E5E1D8] px-6 py-5 max-h-[80vh] overflow-y-auto shadow-lg">
          <div className="flex flex-col gap-0.5">
            {[...PRIMARY_LINKS, ...PARTECIPA_LINKS, ...SECONDARY_LINKS].map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-base font-semibold py-3 border-b border-[#F0EDE6] last:border-0 ${isActive ? "text-[#2E4F3B]" : "text-[#1A1A1A]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/partecipa" data-testid="mobile-nav-cta" className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#C15C3D] text-white font-semibold px-5 py-3.5">
              Partecipa
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
